import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Battery, Brain, Zap, BookOpen, Clock, ShieldCheck } from 'lucide-react';
import { PurchaseSection } from '../components/PurchaseSection';
import { TestimonialCard } from '../components/TestimonialCard';
import { SocialProofPopup } from '../components/SocialProofPopup';
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/e57rPd5ff8kbiFKjibTKKW/ChatGPT_Image_20_ene_2026,_11_39_12.png';
const BONUS_1 = 'https://cdn.magicpatterns.com/uploads/aY2m2MSz8yw2Gf2P942XRw/ChatGPT_Image_20_ene_2026,_12_16_22.png';
const BONUS_2 = 'https://cdn.magicpatterns.com/uploads/nrKzeCEvMb9wgEFuwrgooq/ChatGPT_Image_20_ene_2026,_14_31_52.png';
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden" data-id="element-40">
      <SocialProofPopup data-id="element-41" />

      {/* Hero Section */}
      <header className="bg-slate-900 text-white pt-12 pb-20 px-4 relative overflow-hidden" data-id="element-42">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-43"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-44"></div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-45">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-46">
            <div className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-bold mb-6 border border-amber-500/30" data-id="element-47">
              MÉTODO DE 7 DÍAS
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6" data-id="element-48">
              Estrés Bajo, <br data-id="element-49" />
              <span className="text-amber-500" data-id="element-50">Resultados Altos</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed" data-id="element-51">
              El método simple para rendir mejor en el trabajo sin vivir
              agotado.
            </p>
            <button onClick={scrollToPurchase} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg shadow-amber-500/30 transition-all transform hover:scale-105" data-id="element-52">
              QUIERO RECUPERAR MI ENERGÍA
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative cursor-pointer group" onClick={scrollToPurchase} data-id="element-53">
            <div className="absolute inset-0 bg-amber-500 rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" data-id="element-54"></div>
            <img src={MAIN_IMAGE} alt="Estrés Bajo, Resultados Altos Libro Digital" className="relative rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-300 border-2 border-slate-700" data-id="element-55" />
          </motion.div>
        </div>
      </header>

      {/* Main Copy Section */}
      <main className="max-w-3xl mx-auto px-4 py-16" data-id="element-56">
        {/* The Problem */}
        <section className="mb-16 space-y-6 text-lg md:text-xl text-slate-700 leading-relaxed" data-id="element-57">
          <p className="font-bold text-2xl text-slate-900" data-id="element-58">Vivimos cansados.</p>
          <p data-id="element-59">
            No porque seamos vagos. No porque no tengamos ambición. No porque no
            sepamos lo que hay que hacer.
          </p>
          <p className="border-l-4 border-amber-500 pl-4 italic text-slate-600" data-id="element-60">
            Vivimos cansados porque el estrés nos está robando energía en
            silencio.
          </p>

          <div className="grid gap-4 my-8" data-id="element-61">
            {['Dormís… pero te levantás igual de agotado.', 'Trabajás… pero rendís menos de lo que sabés que podrías.', 'Cumplís… pero terminás el día vacío.'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-slate-100" data-id="element-62">
                <Battery className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-63" />
                <span data-id="element-64">{item}</span>
              </div>)}
          </div>

          <p data-id="element-65">Y lo más peligroso es esto: te acostumbrás.</p>
          <p data-id="element-66">Seguís funcionando. Seguís respondiendo. Seguís avanzando.</p>
          <p className="font-semibold" data-id="element-67">Pero cada vez más forzado.</p>

          <div className="bg-slate-900 text-white p-8 rounded-2xl my-12 text-center" data-id="element-68">
            <p className="text-xl mb-4" data-id="element-69">
              Y entonces aparece la pregunta incómoda:
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-amber-400" data-id="element-70">
              ¿Este es mi límite… o simplemente estoy exhausto?
            </h3>
          </div>

          <p data-id="element-71">
            La mayoría cree que el problema es la motivación. Otros piensan que
            necesitan más disciplina. Algunos apuestan al café, a la presión o a
            trabajar más horas.
          </p>
          <p className="font-bold text-red-600" data-id="element-72">Pero no.</p>
          <p data-id="element-73">
            El verdadero problema no es que no puedas. Es que estás drenado.
          </p>
        </section>

        {/* Symptoms */}
        <section className="mb-16" data-id="element-74">
          <h3 className="text-2xl font-bold mb-6 text-slate-900" data-id="element-75">
            El cansancio moderno no siempre se siente como cansancio.
          </h3>
          <p className="mb-6 text-lg" data-id="element-76">A veces se siente como:</p>

          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-77">
            {[{
            icon: Brain,
            text: 'Cabeza saturada'
          }, {
            icon: Zap,
            text: 'Falta de foco'
          }, {
            icon: ArrowDown,
            text: 'Errores tontos'
          }, {
            icon: Battery,
            text: 'Energía que nunca alcanza'
          }].map((item, i) => <div key={i} className="bg-slate-100 p-4 rounded-xl flex flex-col items-center text-center gap-2" data-id="element-78">
                <item.icon className="w-8 h-8 text-slate-600" data-id="element-79" />
                <span className="font-medium" data-id="element-80">{item.text}</span>
              </div>)}
          </div>

          <p className="text-lg mb-4" data-id="element-81">
            Y lo peor: nadie te enseñó a gestionarlo.
          </p>
          <p className="text-lg mb-4" data-id="element-82">
            No necesitás cambiar de trabajo. No necesitás tomarte un mes
            sabático. No necesitás reinventar tu vida.
          </p>
          <p className="text-2xl font-bold text-amber-600" data-id="element-83">
            Necesitás ordenar tu sistema interno.
          </p>
        </section>

        {/* The Solution */}
        <section className="mb-16 bg-white p-8 rounded-3xl shadow-xl border border-slate-100" data-id="element-84">
          <p className="text-center text-slate-500 uppercase tracking-widest text-sm font-bold mb-4" data-id="element-85">
            Por eso se creó
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-slate-900 mb-2" data-id="element-86">
            ESTRÉS BAJO, RESULTADOS ALTOS
          </h2>
          <p className="text-center text-xl text-amber-600 font-medium mb-8" data-id="element-87">
            El método de 7 días para rendir mejor en el trabajo sin vivir
            agotado
          </p>

          <div className="prose prose-lg text-slate-700 mx-auto" data-id="element-88">
            <p data-id="element-89">
              Un método simple, realista y aplicable para personas reales. Con
              trabajos reales. Con cansancio real. Y con cero tiempo para
              complicarse.
            </p>
            <p data-id="element-90">Este no es un ebook motivacional. No es teoría. No es humo.</p>
            <p className="font-bold" data-id="element-91">
              Es una guía práctica, día por día, con acciones simples que
              liberan energía en lugar de consumirla.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-16" data-id="element-92">
          <p className="text-lg mb-6" data-id="element-93">
            Porque el problema no es la falta de capacidad. Es el estrés
            invisible acumulado que drena energía sin hacer ruido.
          </p>

          <div className="bg-slate-50 border-l-4 border-amber-500 p-6 mb-8" data-id="element-94">
            <h4 className="font-bold mb-4" data-id="element-95">Este método actúa sobre:</h4>
            <ul className="space-y-2" data-id="element-96">
              {['El ruido mental constante', 'La sobrecarga cognitiva diaria', 'Los micro-desperdicios de energía', 'El agotamiento que no se nota… hasta que pesa'].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-97">
                  <div className="w-2 h-2 bg-amber-500 rounded-full" data-id="element-98" />
                  <span data-id="element-99">{item}</span>
                </li>)}
            </ul>
          </div>

          <h3 className="text-2xl font-bold mb-6" data-id="element-100">
            En solo 7 días vas a empezar a:
          </h3>
          <div className="grid gap-3 mb-8" data-id="element-101">
            {['Bajar el ruido mental', 'Recuperar claridad', 'Concentrarte sin forzarte', 'Trabajar mejor en menos tiempo', 'Terminar el día con energía disponible'].map((item, i) => <div key={i} className="flex items-center gap-3 text-lg" data-id="element-102">
                <ShieldCheck className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-103" />
                <span data-id="element-104">{item}</span>
              </div>)}
          </div>

          <p className="text-xl font-medium text-center my-8" data-id="element-105">
            Sin fuerza de voluntad. Sin rutinas imposibles. Sin cambiar tu vida.
          </p>

          <div className="text-center space-y-4" data-id="element-106">
            <p className="text-lg" data-id="element-107">
              No necesitás acelerar más. Necesitás soltar el freno de mano.
            </p>
            <p className="text-lg" data-id="element-108">
              Tu mente es como un navegador con demasiadas pestañas abiertas.
              Este método cierra las que no necesitás… sin apagar la
              computadora.
            </p>
            <p className="text-2xl font-bold text-slate-900" data-id="element-109">
              No estás roto. Estás drenado.
            </p>
            <p className="text-lg" data-id="element-110">Y eso se puede revertir.</p>
            <p className="text-xl font-bold text-amber-600" data-id="element-111">
              Porque cuando el estrés baja, los resultados suben solos.
            </p>
          </div>
        </section>

        {/* Bonuses Section */}
        <section className="py-12 border-t border-slate-200" data-id="element-112">
          <div className="text-center mb-10" data-id="element-113">
            <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide" data-id="element-114">
              Gratis por hoy
            </span>
            <h3 className="text-3xl font-bold mt-4 mb-2" data-id="element-115">Bonos Exclusivos</h3>
            <p className="text-slate-600" data-id="element-116">
              Complementos perfectos para potenciar tus resultados
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-117">
            <motion.div whileHover={{
            y: -5
          }} className="cursor-pointer" onClick={scrollToPurchase} data-id="element-118">
              <img src={BONUS_1} alt="Bono 1" className="rounded-xl shadow-lg mb-4 w-full" data-id="element-119" />
              <h4 className="font-bold text-lg mb-2" data-id="element-120">
                Cómo Rendir Más Sin Café
              </h4>
              <p className="text-sm text-slate-600" data-id="element-121">
                El sistema moderno para energía mental estable y foco real.
              </p>
            </motion.div>
            <motion.div whileHover={{
            y: -5
          }} className="cursor-pointer" onClick={scrollToPurchase} data-id="element-122">
              <img src={BONUS_2} alt="Bono 2" className="rounded-xl shadow-lg mb-4 w-full" data-id="element-123" />
              <h4 className="font-bold text-lg mb-2" data-id="element-124">El Error Silencioso</h4>
              <p className="text-sm text-slate-600" data-id="element-125">
                Por qué vivís cansado sin estarlo físicamente y cómo
                recuperarte.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12" data-id="element-126">
          <h3 className="text-3xl font-bold text-center mb-10" data-id="element-127">
            Lo que dicen quienes ya lo aplicaron
          </h3>
          <div className="grid gap-6" data-id="element-128">
            <TestimonialCard name="Javier Méndez" role="Desarrollador Senior" city="Buenos Aires" quote="Pensé que necesitaba vacaciones, pero solo necesitaba ordenar mi cabeza. En 3 días noté la diferencia en mi foco." delay={0.1} data-id="element-129" />
            <TestimonialCard name="Ana Torres" role="Project Manager" city="Santiago" quote="Increíble cómo pequeños cambios liberan tanta energía. Ya no llego muerta a casa después del trabajo." delay={0.2} data-id="element-130" />
            <TestimonialCard name="Carlos Ruiz" role="Emprendedor" city="Bogotá" quote="Directo al grano. Sin relleno. Exactamente lo que necesitaba para dejar de procrastinar por cansancio." delay={0.3} data-id="element-131" />
            <TestimonialCard name="Laura Gómez" role="Diseñadora UX" city="México DF" quote="Me sentía culpable por no rendir. Este libro me hizo entender que mi cerebro estaba saturado, no roto." delay={0.4} data-id="element-132" />
            <TestimonialCard name="Pablo Silva" role="Arquitecto" city="Montevideo" quote="La mejor inversión que hice este año. Recuperé mis tardes y mi claridad mental." delay={0.5} data-id="element-133" />
          </div>
        </section>

        {/* Final CTA Logic */}
        <div className="text-center mb-12" data-id="element-134">
          <p className="text-xl mb-6" data-id="element-135">Ahora te pregunto algo simple:</p>
          <p className="text-2xl font-medium text-slate-800 mb-8" data-id="element-136">
            Si pudieras rendir mejor, sentirte más liviano y terminar el día con
            energía para vos… <br data-id="element-137" />
            <span className="font-bold" data-id="element-138">¿por qué seguir viviendo agotado?</span>
          </p>
        </div>
      </main>

      {/* Purchase Section */}
      <PurchaseSection id="purchase-section" data-id="element-139" />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-500 py-8 text-center text-sm" data-id="element-140">
        <p data-id="element-141">
          © 2026 Estrés Bajo, Resultados Altos. Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}